import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { isHttpSecure } from "lib/utils/utils";

export type AudioTrack = {
    id: string;
    name: string;
    filename: string;
    duration_ms: number;
    size_bytes: number;
    created_at: string;
};

const defaultHost =
    process.env.REACT_APP_SINGLE_ARC_HOSTNAME ||
    (typeof window !== "undefined" ? window.location.hostname : "");

function tracksBaseUrl(arcId?: string | null, proxyHost?: string | null): string | null {
    const host = arcId && proxyHost ? proxyHost : defaultHost;
    return gatewayServiceBaseUrl("audioTracks", arcId, host);
}

function requireBase(arcId?: string | null, proxyHost?: string | null): string {
    const base = tracksBaseUrl(arcId, proxyHost);
    if (!base) {
        // Direct fallback when gateway helper returns null (e.g. local HTTP ARC).
        const scheme = isHttpSecure() ? "https" : "http";
        return `${scheme}://${defaultHost}/api/speaker-tracks`;
    }
    return base.replace(/\/$/, "");
}

export async function listAudioTracks(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<AudioTrack[]> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/tracks`);
    if (!res.ok) {
        throw new Error(`Failed to list audio tracks (${res.status})`);
    }
    const data = await res.json();
    return Array.isArray(data?.tracks) ? data.tracks : [];
}

export async function uploadAudioTrack(
    file: File,
    name?: string,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<AudioTrack> {
    const fd = new FormData();
    fd.append("file", file);
    if (name?.trim()) fd.append("name", name.trim());
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/tracks`, {
        method: "POST",
        body: fd,
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to upload audio (${res.status})`);
    }
    const data = await res.json();
    if (!data?.track) throw new Error("Upload response missing track");
    return data.track as AudioTrack;
}

export async function deleteAudioTrack(
    trackId: string,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(
        `${requireBase(arcId, proxyHost)}/tracks/${encodeURIComponent(trackId)}`,
        { method: "DELETE" },
    );
    if (!res.ok) {
        throw new Error(`Failed to delete audio track (${res.status})`);
    }
}

export async function previewAudioTrack(
    trackId: string,
    volume = 80,
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/preview/play`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ track_id: trackId, volume }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to play audio (${res.status})`);
    }
}

export async function stopPreviewAudio(
    arcId?: string | null,
    proxyHost?: string | null,
): Promise<void> {
    const res = await authedFetch(`${requireBase(arcId, proxyHost)}/preview/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Failed to stop audio (${res.status})`);
    }
}
